import React, { FC, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import {
  Lights,
  CanvasPlane,
  CanvasBackground,
  Planet,
  Dialog,
} from '../../../../components';
import { ControlledShip } from './ControlledShip';
import { ControlledSwarm } from './ControlledSwarm';
import { useMemo } from 'react';
import { MainGameLoop } from './game';
import { ControlledBullets } from './ControlledBullets';
import { useEffect } from 'react';
import { NUM_LEVELS_PER_STAGE, STAGES } from '../../../../traits';
import LifeBar from './LifeBar';
import { useCamera } from '../../../../utils';
import { ShipConfig, Vector } from '../../../../types';
import { music } from '../../../../audio';
import ControlledPowerup from './ControlledPowerup';

export type MainProps = {
  level: number;
  shipConfig: ShipConfig;
  matrix: number[][];
  colors: string[];
  onComplete: (target: number) => void;
};

const fov = 60;
const cameraPosition: Vector = [0, -60, 100];
const cameraTarget: [number, number, number] = [0, 100, 0];

export const Main: FC<MainProps> = ({ level, shipConfig, onComplete }) => {
  const gameLoop = useMemo(
    () =>
      new MainGameLoop({
        shipConfig,
      }),
    [shipConfig],
  );
  const planeRef = useRef<any>();
  const gameOverRef = useRef<any>();
  const scoreRef = useRef<any>();
  const lifeBarRef = useRef<any>();
  const gameOverInstructionsRef = useRef<any>();
  const scoreTextRef = useRef<string>('00000');

  const stageNumber = Math.floor(level / NUM_LEVELS_PER_STAGE);
  const stage = STAGES[stageNumber];
  const { planet, planeTexture } = stage.levels[level % NUM_LEVELS_PER_STAGE];

  useCamera(cameraPosition, cameraTarget, fov);

  const [xMult, yMult, zMult] = useMemo<[number, number, number]>(
    () => [Math.random() * 4, Math.random() * 4, Math.random() * 4],
    [],
  );
  const amt = 1;

  const canRestartRef = useRef<boolean>(false);
  useEffect(() => {
    const onKeypress = () => {
      if (canRestartRef.current) {
        onComplete(-1);
        music.stop();
      }
    };
    window.addEventListener('keypress', onKeypress);
    return () => window.removeEventListener('keypress', onKeypress);
  }, [onComplete]);

  useEffect(() => {
    if (level === 0 && stageNumber === 0) return;
    gameLoop.swarm.velocity = gameLoop.swarm.velocity.map(v =>
      v ? (v < 0 ? v - Math.random() : v + Math.random()) : v,
    ) as Vector;
  }, [level, stageNumber, gameLoop.swarm]);

  useFrame((state, delta) => {
    gameLoop.step(delta);
    scoreTextRef.current = `${Math.max(0, gameLoop.score)}`.padStart(5, '0');

    // make camera follow ship forward when it blasts off into hyperspace
    if (gameLoop.ship.blastOff) {
      state.camera.position.y = gameLoop.ship.position[1] / 1.25 - 30;
      planeRef.current.position.z = -gameLoop.ship.position[1] / 5 - 10;
      if (state.camera.position.y > 4000) {
        onComplete(0);
      }
    } else if (gameLoop.ship.dead) {
      gameOverRef.current.material.visible =
        gameOverInstructionsRef.current.material.visible = true;
      gameOverRef.current.material.opacity =
        gameOverInstructionsRef.current.material.opacity = Math.min(
          1,
          gameOverRef.current.material.opacity + 0.02,
        );
      scoreRef.current.material.opacity = scoreRef.current.material.opacity =
        Math.max(0, scoreRef.current.material.opacity - 0.02);
      lifeBarRef.current.material.opacity =
        lifeBarRef.current.material.opacity = Math.max(
          0,
          lifeBarRef.current.material.opacity - 0.005,
        );
      state.camera.position.z = state.camera.position.z * 0.99;
      if (state.camera.position.z > 20) {
        state.camera.lookAt(...cameraTarget);
        state.camera.updateProjectionMatrix();
      }
      if (state.camera.position.z < 20) {
        canRestartRef.current = true;
      }
    } else {
      state.camera.position.x =
        Math.sin(state.clock.elapsedTime * xMult) * amt + cameraPosition[0];
      state.camera.position.y =
        Math.cos(state.clock.elapsedTime * yMult) * amt + cameraPosition[1];
      state.camera.position.z =
        Math.sin(state.clock.elapsedTime * zMult) * amt + cameraPosition[2];
    }
  });

  return (
    <>
      <Lights />
      <ControlledPowerup powerupEntity={gameLoop.powerup} />
      <Planet position={[-450, 800, -30]} {...planet} />
      <CanvasBackground texture={stage.background} />
      <CanvasPlane
        meshRef={el => (planeRef.current = el)}
        texture={planeTexture}
        position={[0, 100, -10]}
        size={[900, 400]}
      />
      <ControlledSwarm
        swarmEntity={gameLoop.swarm}
        shipEntity={gameLoop.ship}
      />
      <ControlledShip
        shipConfig={shipConfig}
        position={[0, 0, 0]}
        shipEntity={gameLoop.ship}
      />
      <ControlledBullets color="#a3d6ff" bulletsEntity={gameLoop.bullets} />
      <ControlledBullets
        color="#f00"
        size={[2, 5, 2]}
        bulletsEntity={gameLoop.enemyBullets}
      />
      <group>
        <Dialog
          meshRef={el => (scoreRef.current = el)}
          text="00000"
          textRef={scoreTextRef}
          position={[-75, -1, -6]}
          rotation={[1, 0, 0]}
          size={[30, 10]}
          canvasSize={[60, 20]}
          color={[255, 255, 255, 255]}
        />
        <LifeBar
          ship={gameLoop.ship}
          meshRef={(el: any) => (lifeBarRef.current = el)}
        />
        <Dialog
          text={planet.name}
          right
          position={[75, -1, -6]}
          rotation={[1, 0, 0]}
          size={[30, 10]}
          canvasSize={[60, 20]}
          color={[255, 255, 255, 255]}
        />
      </group>
      <Dialog
        meshRef={el => (gameOverRef.current = el)}
        text="GAME OVER"
        position={[0, 100, 10]}
        size={[600, 100]}
        canvasSize={[120, 20]}
        visible={false}
        opacity={0}
        center
        color={stage.background.dark ? [255, 255, 255, 255] : [0, 0, 0, 255]}
      />
      <Dialog
        meshRef={el => (gameOverInstructionsRef.current = el)}
        text="press any key to continue"
        position={[0, -30, -5]}
        size={[18, 2]}
        canvasSize={[180, 20]}
        visible={false}
        opacity={0}
        center
        background="white"
      />
    </>
  );
};
